import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';

import { FilingConductorDataModelCountResponse, FilingConductorProcessedLogDetail, FilingConductorProcessedLogResponse } from './types';

export interface UseFilingConductorProcessedLogsParams {
  corporationId: number | null;
  /** Inclusive analysis-window bounds (ISO date). */
  fromDate: string | null;
  toDate: string | null;
  /** Defer the (potentially large) fetch until the modal is open. */
  enabled?: boolean;
}

function buildKey(corporationId: number | null, fromDate: string | null, toDate: string | null, enabled: boolean, suffix: string) {
  const canFetch = enabled && corporationId !== null && !!fromDate && !!toDate;
  if (!canFetch) return null;
  const query = new URLSearchParams({ fromDate: fromDate as string, toDate: toDate as string }).toString();
  return `filing-conductor/corporations/${corporationId}/processed-logs${suffix}?${query}`;
}

/** Total deduped ProcessedLog count for the card (no row payload). */
export function useFilingConductorProcessedLogCount({ corporationId, fromDate, toDate, enabled = true }: UseFilingConductorProcessedLogsParams) {
  const key = buildKey(corporationId, fromDate, toDate, enabled, '/count');
  const { data, error, isLoading, mutate } = useSWR<FilingConductorDataModelCountResponse>(key, SWRFetcher({ method: 'GET' }));
  return { count: data?.count ?? null, isLoading, error, mutate };
}

/** Full deduped ProcessedLog row set; filters + pagination are applied client-side. */
export function useFilingConductorProcessedLogs({ corporationId, fromDate, toDate, enabled = true }: UseFilingConductorProcessedLogsParams) {
  const key = buildKey(corporationId, fromDate, toDate, enabled, '');
  const { data, error, isLoading, mutate } = useSWR<FilingConductorProcessedLogResponse>(key, SWRFetcher({ method: 'GET' }), { keepPreviousData: true });
  return { rows: data?.rows ?? [], totalCount: data?.totalCount ?? 0, isLoading, error, mutate };
}

/** Full ProcessedLog DB row (incl. snapshotJson) for the row-detail modal; fetched only when an id is set. */
export function useFilingConductorProcessedLogDetail({ corporationId, id }: { corporationId: number | null; id: string | null }) {
  const key = corporationId !== null && id ? `filing-conductor/corporations/${corporationId}/processed-logs/${id}` : null;
  const { data, error, isLoading } = useSWR<FilingConductorProcessedLogDetail>(key, SWRFetcher({ method: 'GET' }));
  return { detail: data ?? null, isLoading, error };
}
